import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Loader2 } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { Button } from '@besiktning/ui'
import { Logo } from '../components/Logo'
import { t } from '../i18n'

export default function MembershipSelection() {
  const navigate = useNavigate()
  const { memberships, currentMembership, selectMembership, requiresMembershipSelection, isInitializing } = useAuthStore()
  const [selecting, setSelecting] = React.useState<number | null>(null)

  // Show loading while initializing
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  // If no memberships, show error
  if (memberships.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-600">{t('membershipSelection.noCompaniesFound')}</p>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mt-4"
          >
            {t('membershipSelection.goToDashboard')}
          </Button>
        </div>
      </div>
    )
  }

  // If only one membership and it's already selected, redirect to home
  if (memberships.length === 1 && currentMembership) {
    navigate('/')
    return null
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return t('membershipSelection.roleOwner')
      case 'manager': return t('membershipSelection.roleManager')
      case 'mechanic': return t('membershipSelection.roleMechanic')
      case 'staff': return t('membershipSelection.roleStaff')
      default: return role
    }
  }

  const handleSelect = async (membership: typeof memberships[0]) => {
    if (currentMembership?.type === membership.type && currentMembership?.id === membership.id) {
      navigate('/')
      return
    }

    setSelecting(membership.id)
    try {
      await selectMembership(membership.type, membership.id)
      navigate('/')
    } catch (error) {
      console.error('Failed to select membership:', error)
    } finally {
      setSelecting(null)
    }
  }

  const isCurrentMembership = (membership: typeof memberships[0]) => {
    return currentMembership?.type === membership.type && currentMembership?.id === membership.id
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <Logo className="h-12 sm:h-14" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {requiresMembershipSelection ? t('membershipSelection.headingSelect') : t('membershipSelection.headingSwitch')}
          </h1>
          <p className="text-gray-600 mt-2">
            {requiresMembershipSelection
              ? t('membershipSelection.descMultiple')
              : t('membershipSelection.descSwitch')}
          </p>
        </div>

        <div className="space-y-3">
          {memberships.map((membership) => {
            const isSelected = isCurrentMembership(membership)
            const isLoading = selecting === membership.id

            return (
              <button
                key={`${membership.type}-${membership.id}`}
                onClick={() => handleSelect(membership)}
                disabled={isLoading}
                className={`w-full flex items-center p-4 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                  membership.type === 'garage' ? 'bg-blue-100' : 'bg-green-100'
                }`}>
                  {membership.type === 'garage' ? (
                    <span className="text-xl">🔧</span>
                  ) : (
                    <span className="text-xl">✨</span>
                  )}
                </div>

                <div className="ml-4 flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {membership.business.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      membership.type === 'garage' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {membership.type === 'garage' ? t('membershipSelection.typeGarage') : t('membershipSelection.typeCarCare')}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getRoleLabel(membership.role)}
                    </span>
                  </div>
                  {membership.business.kommun && (
                    <p className="text-xs text-gray-400 mt-1">
                      {membership.business.kommun}
                    </p>
                  )}
                </div>

                <div className="ml-4 flex-shrink-0">
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                  ) : isSelected ? (
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {!requiresMembershipSelection && (
          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
            >
              {t('membershipSelection.cancel')}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
